import { isEmpty, isArray, isArrayLikeObject, isObjectLike, includes, isNil, find, isFinite } from 'lodash';

import * as DataOrigins from 'commons-vehicle/constants/dataOrigins.js';
import { DATA_TYPES, SUPPLIER_OPTION } from 'commons-vehicle/constants/dataTypes.js';
import { EXTERIOR_COLOUR, INTERIOR_TRIM } from 'commons-vehicle/Quote/constants/modelTrimItemCategories.js';
import OptionsPackageModel from 'commons-vehicle/ModelYear/models/OptionsPackageModel.js';
import * as PurchaseMethodsService from 'commons-vehicle/PurchaseMethods/services/PurchaseMethodsService/PurchaseMethodsService.js';
import {
    resetQuoteItemCategoriesFromModelTrimOptions,
    resetQuoteItemCategoriesFromOptionsValidation,
    selectQuoteItemPackage,
} from 'commons-vehicle/Quote/services/QuoteItemCategoryService/QuoteItemCategoryService.js';

const { FEATURE } = DATA_TYPES;

function resetOptionsPackageFromModelData(modelData, disclaimers) {
    const selectionCode =
        `${modelData.configuration.cabSizeSelectionCode}` +
        `${modelData.configuration.packageSelectionCode}|${modelData.configuration.id}`;

    const optionsPackage = new OptionsPackageModel({
        trimsBitField: modelData.configuration.trimsBitField,
        trimsBitFields: modelData.configuration.trimsBitFields,
        selectionCode,
        enabled: true,
        visible: true,
        selected: false,
        userSelected: false,
        code: modelData.configuration.packageCode,
        description: modelData.configuration.package.description,
        rank: modelData.configuration.rank,
        configuration: modelData.configuration,
    });

    return optionsPackage.setIn(['configuration', 'purchaseMethods', 'disclaimers'], disclaimers);
}

export function resetOptionsPackageFromOriginData(dataOrigin, originOptionsPackageData, disclaimers) {
    if (isEmpty(originOptionsPackageData) || !isObjectLike(originOptionsPackageData)) {
        return new OptionsPackageModel();
    }

    switch (dataOrigin) {
        case DataOrigins.MODELS_COLLECTION:
            return new OptionsPackageModel();

        case DataOrigins.MODEL_YEAR_POWERS_TRIMS:
            return resetOptionsPackageFromModelData(originOptionsPackageData, disclaimers);

        default:
            return new OptionsPackageModel();
    }
}

export function resetOptionsPackageExteriorColours(optionsPackage, exteriorColourSelectionCode) {
    const configuration = optionsPackage.configuration;
    const exteriorColourCategory = find(configuration.options, (optionCategory) => {
        return optionCategory.id === EXTERIOR_COLOUR;
    });
    const selectedOption = exteriorColourCategory
        ? find(exteriorColourCategory.itemsPackages, (itemsPackage) => {
              return itemsPackage.selectionCode === `${exteriorColourSelectionCode}>${SUPPLIER_OPTION}`;
          })
        : null;

    const exteriorColours = configuration.exteriorColours.map((colour) =>
        colour.set('selected', colour.colourCode === exteriorColourSelectionCode)
    );

    let options = configuration.options;
    if (!isNil(selectedOption)) {
        options = selectQuoteItemPackage(configuration.options, exteriorColourCategory, selectedOption);
    }

    return optionsPackage
        .setIn(['configuration', 'exteriorColours'], exteriorColours)
        .setIn(['configuration', 'options'], options);
}

export function resetOptionsPackageInteriorColours(optionsPackage, interiorColourSelectionCode) {
    const configuration = optionsPackage.configuration;
    const interiorColourCategory = find(configuration.options, (optionCategory) => {
        return optionCategory.id === INTERIOR_TRIM;
    });
    const selectedOption = interiorColourCategory
        ? find(interiorColourCategory.itemsPackages, (itemsPackage) => {
              return itemsPackage.selectionCode === `${interiorColourSelectionCode}>${SUPPLIER_OPTION}`;
          })
        : null;

    const interiorColours = configuration.interiorColours.map((colour) =>
        colour.set('selected', colour.colourCode === interiorColourSelectionCode)
    );

    let options = configuration.options;
    if (!isNil(selectedOption)) {
        options = selectQuoteItemPackage(configuration.options, interiorColourCategory, selectedOption);
    }

    return optionsPackage
        .setIn(['configuration', 'interiorColours'], interiorColours)
        .setIn(['configuration', 'options'], options);
}

export function resetOptionsPackageOptionsAvailability(optionsPackage, availableOptionsCodes, selectedOptions) {
    const validSelectedOptions = isEmpty(selectedOptions) || isArrayLikeObject(selectedOptions);
    if (!isArray(availableOptionsCodes) || !validSelectedOptions) {
        return optionsPackage;
    }

    const selectedExteriorColour = find(selectedOptions, (selectedOption) => {
        return selectedOption.typeID === FEATURE.TYPES.EXTERIOR_COLOUR;
    });
    const selectedExteriorColourCode = selectedExteriorColour ? selectedExteriorColour.code : '';
    const exteriorColourOptionsPackage = optionsPackage.configuration.exteriorColours.map((colour) =>
        colour
            .set('enabled', includes(availableOptionsCodes, colour.colourCode))
            .set('selected', colour.colourCode === selectedExteriorColourCode)
    );

    const selectedInteriorColour = find(selectedOptions, (selectedOption) => {
        return selectedOption.typeID === FEATURE.TYPES.INTERIOR_TRIM;
    });
    const selectedInteriorColourCode = selectedInteriorColour ? selectedInteriorColour.code : '';
    const interiorColourOptionsPackage = optionsPackage.configuration.interiorColours.map((colour) =>
        colour
            .set('enabled', includes(availableOptionsCodes, colour.colourCode))
            .set('selected', colour.colourCode === selectedInteriorColourCode)
    );

    const options = resetQuoteItemCategoriesFromOptionsValidation(
        optionsPackage.configuration.options,
        selectedOptions
    );

    if (optionsPackage.selected) {
        const newOptionsPackage = optionsPackage
            .setIn(['configuration', 'exteriorColours'], exteriorColourOptionsPackage)
            .setIn(['configuration', 'interiorColours'], interiorColourOptionsPackage)
            .setIn(['configuration', 'options'], options);

        let configPurchaseMethods = PurchaseMethodsService.resetSelectedOptions(
            newOptionsPackage.configuration.purchaseMethods,
            newOptionsPackage.configuration.getSelectedOptions(),
            newOptionsPackage.configuration.configurationOptions
        );

        configPurchaseMethods = configPurchaseMethods.set('optionsSelectionResolutions', null);
        return newOptionsPackage.setIn(['configuration', 'purchaseMethods'], configPurchaseMethods);
    }
    return optionsPackage
        .setIn(['configuration', 'exteriorColours'], exteriorColourOptionsPackage)
        .setIn(['configuration', 'interiorColours'], interiorColourOptionsPackage)
        .setIn(['configuration', 'purchaseMethods', 'optionsSelectionResolutions'], null);
}

export function resetOptionsPackageOptionsSelectionResolutions(optionsPackage, resolutions) {
    if (!optionsPackage.selected || !isObjectLike(resolutions) || isEmpty(resolutions.resolutions)) {
        return optionsPackage.setIn(['configuration', 'purchaseMethods', 'optionsSelectionResolutions'], null);
    }

    return optionsPackage.setIn(['configuration', 'purchaseMethods', 'optionsSelectionResolutions'], resolutions);
}

export function resetOptionsPackageAvailableOptions(optionsPackage, availableOptions, configurationOptions) {
    if (optionsPackage.selected) {
        return optionsPackage
            .setIn(['configuration', 'options'], resetQuoteItemCategoriesFromModelTrimOptions(availableOptions))
            .setIn(['configuration', 'configurationOptions'], configurationOptions)
            .setIn(['configuration', 'purchaseMethods', 'availableOptions'], availableOptions)
            .setIn(['configuration', 'purchaseMethods', 'configurationOptions'], configurationOptions)
            .setIn(['configuration', 'purchaseMethods', 'optionsSelectionResolutions'], null);
    }
    return optionsPackage;
}

export function resetOptionsPackageOptions(optionsPackage, options) {
    if (optionsPackage.selected) {
        const newOptionsPackage = optionsPackage.setIn(['configuration', 'options'], options);
        const selectedOptions = newOptionsPackage.configuration.getSelectedOptions();
        const purchaseMethods = PurchaseMethodsService.resetSelectedOptions(
            optionsPackage.configuration.purchaseMethods,
            selectedOptions,
            optionsPackage.configuration.configurationOptions
        );

        return newOptionsPackage.setIn(['configuration', 'purchaseMethods'], purchaseMethods);
    }
    return optionsPackage;
}

export function resetOptionsPackagePurchaseMethod(optionsPackage, purchaseMethod) {
    return optionsPackage.setIn(
        ['configuration', 'purchaseMethods'],
        PurchaseMethodsService.selectPurchaseMethod(optionsPackage.configuration.purchaseMethods, purchaseMethod)
    );
}

export function recalculateOptionsPackagePayments(optionsPackage, paymentCalculationParameters) {
    return optionsPackage.setIn(
        ['configuration', 'purchaseMethods'],
        PurchaseMethodsService.recalculatePayments(
            optionsPackage.configuration.purchaseMethods,
            paymentCalculationParameters
        )
    );
}

export function recalculateOptionsPackagePurchaseAmounts(optionsPackage, paymentCalculationParameters) {
    const newOptionsPackage = optionsPackage.setIn(
        ['configuration', 'purchaseMethods'],
        PurchaseMethodsService.recalculatePurchaseAmounts(
            optionsPackage.configuration.purchaseMethods,
            paymentCalculationParameters
        )
    );

    return recalculateOptionsPackagePayments(newOptionsPackage, paymentCalculationParameters);
}

export function selectOptionsPackageKMPlan(optionsPackage, kmPlan) {
    const purchaseMethods = optionsPackage.configuration.purchaseMethods;
    const selectedFinance = purchaseMethods.getSelectedFinance();

    if (!selectedFinance) {
        return optionsPackage;
    }

    const newKMPlan = selectedFinance.getKMPlan(kmPlan);
    if (!newKMPlan) {
        return optionsPackage;
    }

    const newPurchaseMethods = purchaseMethods.lease.selected
        ? purchaseMethods
              .setIn(['lease', 'annualKMAllowed'], newKMPlan.annualKMAllowed)
              .setIn(['lease', 'endTermKMCost'], newKMPlan.endTermKMCost)
              .setIn(['lease', 'terms'], newKMPlan.terms)
        : purchaseMethods
              .setIn(['finance', 'annualKMAllowed'], newKMPlan.annualKMAllowed)
              .setIn(['finance', 'endTermKMCost'], newKMPlan.endTermKMCost)
              .setIn(['finance', 'terms'], newKMPlan.terms);

    return optionsPackage.setIn(['configuration', 'purchaseMethods'], newPurchaseMethods);
}

export function resetOptionsPackageEmployeeRebateAdjustment(optionsPackage, rebateAdjustment) {
    if (optionsPackage.selected) {
        const purchaseMethods = PurchaseMethodsService.resetOptionsAmounts(
            optionsPackage.configuration.purchaseMethods.set(
                'employeeRebateAdjustment',
                isFinite(rebateAdjustment) ? rebateAdjustment : 0
            )
        );

        return optionsPackage.setIn(['configuration', 'purchaseMethods'], purchaseMethods);
    }
    return optionsPackage;
}

export function resetOptionsPackagePercentageRebateAdjustment(optionsPackage, rebateAdjustment) {
    if (optionsPackage.selected) {
        const purchaseMethods = PurchaseMethodsService.resetOptionsAmounts(
            optionsPackage.configuration.purchaseMethods.setIn(
                ['cashPurchase', 'optionsAdjustmentDiscounts'],
                isFinite(rebateAdjustment) ? rebateAdjustment : 0
            )
        );

        return optionsPackage.setIn(['configuration', 'purchaseMethods'], purchaseMethods);
    }
    return optionsPackage;
}
export default {
    resetOptionsPackageFromOriginData,
    resetOptionsPackageExteriorColours,
    resetOptionsPackageInteriorColours,
    resetOptionsPackageOptionsAvailability,
    resetOptionsPackageOptionsSelectionResolutions,
    resetOptionsPackageAvailableOptions,
    resetOptionsPackageOptions,
    resetOptionsPackagePurchaseMethod,
    recalculateOptionsPackagePayments,
    recalculateOptionsPackagePurchaseAmounts,
    selectOptionsPackageKMPlan,
    resetOptionsPackageEmployeeRebateAdjustment,
    resetOptionsPackagePercentageRebateAdjustment,
};
